import { calcItemCost, calculateMarkupValue, getGrossMargin, calculatePrice, formatNumber } from '../PricingFunctions'
import React from 'react'

const ColumnTypes = {
  Number: 'number',
  String: 'string',
  Currency: 'currency',
  Percent: 'percent'
}

export const productHeadings = (grouped, taxRates, siteCategories, wholesalerFiles, overrideTypes, types, ageRestrictions) => {
  // we cant use the getMappingsAndValues function for the tax dropdown, as we want the value to be an expression of two elements
  const taxRateMappings = {}
  const taxLookup = {} // this contains a lookup for the tax id to the actual tax rate - we need this for calculating Gross Margin
  const taxRateIds = []

  for (const taxRate of taxRates) {
    taxRateMappings[taxRate.id] = `${taxRate.taxRate}% - ${taxRate.description}`
    taxRateIds.push(taxRate.id)
    taxLookup[taxRate.id] = taxRate.taxRate
  }

  const [categoryMappings, categoryIds] = getMappingsAndValues(siteCategories, 'id', 'nodeListString', true)
  const [wholesalerFileMappings, wholesalerFileIds] = getMappingsAndValues(wholesalerFiles, 'id', 'description', true)
  const [overrideTypeMappings, overrideTypeKeys] = getMappingsAndValues(overrideTypes, 'value', 'description')
  const [typeMappings, typeKeys] = getMappingsAndValues(types, 'value', 'description')
  const [ageRestrictionMappings, ageRestrictionKeys] = getMappingsAndValues(ageRestrictions, 'id', 'age')

  // NOTE - in theory, you should be able to define the cellClassRules in the defaultColDef, but that seemed to cause a bug
  // whereby if you edited a column in a column group, as you finished editing, the group would always collapse for unknown reasons.
  // attaching the cellClassRules on a per column basis seems to fix this - PJF
  return [
    {
      headerName: 'Products',
      children: [
        createReadOnlyColumn('Barcode', 'barcode', 180, ColumnTypes.String, {
          pinned: 'left',
          lockPinned: true,
          lockPosition: 'left',
          headerCheckboxSelection: true,
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true, // this means if we click the 'select all' header option, only currently filtered options will be selected
          lockVisible: true,
          valueFormatter: (params) => params.data.barcode.value === null ? '<Unbarcoded>' : params.data.barcode.value,
          filterValueGetter: (params) => params.data.barcode.value,
          filterParams: {
            defaultOption: 'startsWith'
          }
        })
      ]
    },
    {
      headerName: 'Information',
      columnGroupShow: 'open',
      children: [
        createValidatedColumn('Description', 'product_description', 250, ColumnTypes.String, '^.{2,100}$', 'The value should be between 2 and 100 characters'),
        createValidatedColumn('Short Description', 'product_short_description', 200, ColumnTypes.String, '^.{2,50}$', 'The value should be between 2 and 50 characters'),
        createValidatedColumn('Item Size', 'item_size_desc', 150, ColumnTypes.String, '^.{0,20}$', 'The value should be between 2 and 20 characters'),
        createDropDownColumn('Category', 'category_id', 250, categoryIds, categoryMappings, 450),
        createDropDownColumn('Age Restriction', 'restricted_age_id', 100, ageRestrictionKeys, ageRestrictionMappings, 100),
        createDropDownColumn('Tax Rate', 'tax_id', 220, taxRateIds, taxRateMappings, 200)
      ]
    },
    {
      headerName: 'Wholesaler',
      children: [
        createValidatedColumn('Prod Code', 'product_num', null, ColumnTypes.String, '^[a-zA-Z0-9-]{0,20}$', 'The value should be less than 20 alphanumeric or hyphen characters'),
        createDropDownColumn('Wholesaler File', 'wholesaler_id', 150, wholesalerFileIds, wholesalerFileMappings, 200)
      ]
    },
    {
      headerName: 'Cost',
      children: [
        createCheckboxColumn('PM', 'price_marked', 100),
        createValidatedColumn('Pack Cost', 'pack_cost', null, ColumnTypes.Currency, '^\\d{1,9}$', 'The value must be number less than 1,000,000,000'),
        createValidatedColumn('Pack Size', 'pack_size', null, ColumnTypes.Number, '^\\d{1,9}$', 'The value must be number less than 1,000,000,000'),
        createReadOnlyColumn('Unit Cost', null, null, ColumnTypes.Currency, {
          valueGetter: (params) => {
            // we compute this on the fly so if we edit pack size or cost, it automatically updates
            const packSize = params.data.pack_size.value > 0 ? params.data.pack_size.value : 1
            const itemCost = calcItemCost(params.data.pack_cost.value, packSize)

            return (Math.ceil(itemCost))
          }
        }),
        createValidatedColumn('Unit RRP', 'unit_rrp', null, ColumnTypes.Currency, '^\\d{1,9}$', 'The value must be number less than 1,000,000,000')
      ]
    },
    {
      headerName: 'Pricing',
      children: [
        createDropDownColumn('Type', 'type', 100, typeKeys, typeMappings, 140, {
          editable: false, // originally we allowed this to be changed for user_created products, but now I think this should be immutable to keep things simple. PJF
          valueSetter: (params) => {
            params.data.type.value = params.newValue

            // also reset the markup values to sensible values if we change the type
            if (params.newValue === 'priced') {
              params.data.markup_calculation_type.value = 'Number'
            } else {
              params.data.markup_calculation_type.value = null
            }

            params.data.markup_calculation_value.value = 0

            return (true)
          },
          cellClassRules: {
            overridden: undefined, // we don't display the overridden flag on this field - it will only be editable on user_created products anyway currently
            readonly: (params) => true
          }
        }),
        createValidatedColumn('Markup Value', 'markup_calculation_value', null, ColumnTypes.Number, validateMarkup, 'The value must be number less than 1,000,000,000', {
          valueFormatter: (params) => {
            if (params.data.type.value !== 'priced') {
              return ('N/A')
            } else {
              return (formatMarkupValue(params))
            }
          },
          editable: (params) => params.data.type.value === 'priced',
          cellClassRules: {
            readonly: (params) => params.data.type.value !== 'priced',
            validationFailed: (params) => {
              // because we overwrite the cellClassRules, we have to redefine the validationFailed rule function too
              return (params.data[params.colDef.field].lastValidation && params.data[params.colDef.field].lastValidation !== true)
            }
          }
        }),
        createDropDownColumn('Markup Type', 'markup_calculation_type', 160, overrideTypeKeys, overrideTypeMappings, null, {
          cellClassRules: {
            readonly: (params) => params.data.type.value !== 'priced'
          },
          cellClass: ['markupType'] // there was no easy way to format this cell nicely for an Unpriced column, so we fudge it using CSS
        }),
        // Price is a special column that needs validating, but isn't actually stored as a data point - it actually just works out what the
        // markup_calculation_value would be to get the desired price
        // Because of this, we set it in a more traditional style with custom functions etc
        // We also just create a new key on the row data called priceValidation that we use to store the details of if price is currently valid or not
        {
          headerName: 'Price',
          type: 'numericColumn',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params) => {
            if (params.data.type.value !== 'priced') {
              return ('N/A')
            } else {
              return (currencyFormatter(params))
            }
          },
          editable: (params) => params.data.type.value === 'priced',
          cellClassRules: {
            overridden: isPriceOverridden,
            readonly: (params) => params.data.type.value !== 'priced',
            validationFailed: (params) => {
              return (params.data.priceValidation && params.data.priceValidation !== true)
            }
          },
          valueGetter: getPrice,
          valueSetter: (params) => {
            const markupValue = calculateMarkupValue(params.data.unit_rrp.value, params.data.pack_cost.value, params.data.pack_size.value, params.data.markup_calculation_type.value, params.newValue)

            if (isNaN(markupValue)) {
              params.data.priceValidation = params.newValue
            } else {
              // validation passed - so set the markup calculation value
              params.data.markup_calculation_value.value = markupValue
              params.data.priceValidation = true
            }

            params.api.flashCells({
              rowNodes: [params.node],
              columns: [params.column]
            })

            return (true)
          },
          tooltipValueGetter: (params) => {
            if (!params.data.priceValidation || params.data.priceValidation === true) {
              return ('')
            } else {
              return (`ERROR: '${params.data.priceValidation}' was not a valid value for Price. The value must be number less than 1,000,000,000`)
            }
          }
        },
        createReadOnlyColumn('GM', null, null, ColumnTypes.Percent, {
          valueGetter: (params) => {
            // we have to define this function inline as the taxLookup table is not available to the getGrossMargin function
            const taxRate = taxLookup[params.data.tax_id.value]

            return (getGrossMargin(params.data.markup_calculation_value.value, params.data.markup_calculation_type.value, params.data.unit_rrp.value, params.data.pack_cost.value, params.data.pack_size.value, taxRate))
          },
          filterValueGetter: (params) => {
            const taxRate = taxLookup[params.data.tax_id.value]

            return (getGrossMargin(params.data.markup_calculation_value.value, params.data.markup_calculation_type.value, params.data.unit_rrp.value, params.data.pack_cost.value, params.data.pack_size.value, taxRate))
          },
          filterParams: {
            defaultOption: 'greaterThanOrEqual'
          }
        })
      ]
    },
    {
      headerName: 'Miscellaneous',
      children: [
        createCheckboxColumn('User Created', 'user_created', null, {
          editable: false,
          headerTooltip: 'User Created is a read-only property',
          cellClassRules: {
            overridden: null // we don't want to mark this as overridden or not
          }
        })
      ]
    }
  ]
}

const getPrice = (params) => {
  return (calculatePrice(params.data.markup_calculation_value.value, params.data.markup_calculation_type.value, params.data.unit_rrp.value, params.data.pack_cost.value, params.data.pack_size.value))
}

const formatMarkupValue = (params) => {
  const calcType = params.data.markup_calculation_type.value
  const value = params.value === null ? 0 : params.value

  let amountDP = 0

  switch (calcType) {
    case 'PercentOverRRP':
      amountDP = 1
      break
    case 'PercentOverCost':
      amountDP = 1
      break
    case 'PercentOfRRP':
      amountDP = 1
      break
    case 'PercentOfCost':
      amountDP = 1
      break

      // case 'PercentGM':
      //   newCalcValue = calcGrossMargin(markupValue, taxRate, exactCost)
      //   amountDP = 1
      //   break
    case 'PriceOverRRP':
      amountDP = 0
      break
    case 'PriceOverCost':
      amountDP = 0
      break
    case 'Number':
      amountDP = 0
      break
  }

  return (formatNumber(value, amountDP))
}

// Create our number formatter.
const formatter = new Intl.NumberFormat('en-GB', {
  style: ColumnTypes.Currency,
  currency: 'GBP'

  // These options are needed to round to whole numbers if that's what you want.
  // minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  // maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
})

const currencyFormatter = (params) => {
  if (params.value !== '-') {
    params.value = formatter.format(params.value / 100)
    return params.value
  }
}

const percentFormatter = (params) => {
  if (params.value !== null) {
    params.value = params.value.toFixed(2) + ' %'
    return params.value
  } else {
    return ('-')
  }
}

// function dateFormatter (params) {
//   let result = ''

//   if (params.value.getTime() > 0) {
//     result = params.value.toLocaleDateString('en-GB', { // you can use undefined as first argument
//       year: 'numeric',
//       month: '2-digit',
//       day: '2-digit'
//     })
//   }

//   return result
// }

// take an array of objects containing keys and value elements we want to turn into a drop down
// return an object of keys linked to values, and an array of values sorted into the order you wish them to be displayed.
// if sortValues is false, then they will just be returned in the original order they were passed in
const getMappingsAndValues = (arrayOfObjects, key, value, sortValues) => {
  const mappings = {}
  const values = []

  if (sortValues) {
    arrayOfObjects.sort((a, b) => {
      if (a[value] < b[value]) {
        return (-1)
      }

      if (a[value] > b[value]) {
        return (1)
      }

      return 0
    })
  }

  for (const object of arrayOfObjects) {
    mappings[object[key]] = object[value] + '' // we convert to a string here, as if it isn't one, AG Grid breaks when trying to filter
    values.push(object[key])
  }

  return ([mappings, values])
}

const isCellOverridden = (params) => {
  const columnSelected = params.colDef.field

  return (columnSelected in params.data && params.data[columnSelected].original !== params.value)
}

const isPriceOverridden = (params) => {
  // we don't allow user_created products to be overridden
  if (params.data.user_created.value) {
    return (false)
  }

  let priceOverridden = false

  // if the product is priced, AND has a markup type != PenceOverRRP OR a non-zero markup, we class it as overridden
  if (params.data.type.value === 'priced' &&
     (params.data.markup_calculation_type.value !== 'PriceOverRRP' || Number(params.data.markup_calculation_value.value) !== 0)) {
    priceOverridden = true
  }

  return (priceOverridden)
}

const syncValidateValueSetter = (validation) => (params) => {
  syncValidator(
    params.newValue,
    validation,
    params,
    _onSuccess(params),
    _onFail(params)
  )

  return true // example returned false, but if we don't return true here, it doesn't fire the cellValueChanged event which we need to track changes
}

const _onSuccess = (params) => () => {
  const field = params.colDef.field

  params.data[field].value = params.newValue
  params.data[field].lastValidation = true

  params.api.flashCells({
    rowNodes: [params.node],
    columns: [params.column]
  })
}

const _onFail = (params) => () => {
  const field = params.colDef.field

  params.data[field].lastValidation = params.newValue

  params.api.flashCells({
    rowNodes: [params.node],
    columns: [params.column]
  })
}

const syncValidator = (newValue, validation, params, onSuccess, onFail) => {
  let validated = false

  if (typeof validation === 'string') {
    // if we want to invalidate null / undefined in some way then we have to write a custom function
    if (newValue === undefined || newValue === null) {
      validated = true
    } else {
      const regex = new RegExp(validation)

      if (newValue.match(regex)) {
        validated = true
      }
    }
  } else {
    validated = validation(newValue, params)
  }

  if (validated) {
    onSuccess()
  } else {
    onFail()
  }
}

const createDropDownColumn = (columnName, field, width, dropDownValues, mappings, dropDownWidth, additionalOptions) => {
  const options = {
    headerName: columnName,
    field: field,
    width: width,
    editable: true,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorPopup: true,
    cellEditorParams: {
      values: dropDownValues,
      cellRenderer: (params) => {
        return (<div style={{ fontSize: 14, width: dropDownWidth }}>{params.valueFormatted}</div>)
      }
    },
    comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
      if (mappings[valueA] < mappings[valueB]) {
        return (-1)
      }

      if (mappings[valueA] > mappings[valueB]) {
        return (1)
      }

      return 0
    },
    filter: 'agSetColumnFilter',
    filterParams: {
      showTooltips: true,
      // sort the filter alphabetically
      comparator: (a, b) => {
        if (mappings[a] < mappings[b]) {
          return (-1)
        }

        if (mappings[a] > mappings[b]) {
          return (1)
        }

        return 0
      }
    },
    refData: mappings,
    cellClassRules: {
      overridden: isCellOverridden
    },
    valueGetter: (params) => params.data[params.colDef.field].value,
    valueSetter: (params) => {
      params.data[params.colDef.field].value = params.newValue
      return (true)
    },
    ...additionalOptions // this will overwrite an 'duplicated' keys that are passed in
  }

  return (options)
}

const processColumnType = (columnType) => {
  let type = null // default for a string - there is only types for numericColumn and rightAligned, so just set others to null
  let formatter = null
  let filter = 'agTextColumnFilter'

  if (columnType === ColumnTypes.Number) {
    type = 'numericColumn'
    filter = 'agNumberColumnFilter'
  } else if (columnType === ColumnTypes.Currency) {
    type = 'numericColumn'
    formatter = currencyFormatter
    filter = 'agNumberColumnFilter'
  } else if (columnType === ColumnTypes.Percent) {
    type = 'numericColumn'
    formatter = percentFormatter
    filter = 'agNumberColumnFilter'
  }

  return ([type, filter, formatter])
}

// type: number, string, currency
const createValidatedColumn = (columnName, field, width, columnType, validation, validationFailedMessage, additionalOptions) => {
  const [type, filter, formatter] = processColumnType(columnType)

  const options = {
    headerName: columnName,
    field: field,
    width: width,
    type: type,
    filter: filter,
    editable: true,
    sortable: true,
    valueFormatter: formatter,
    valueGetter: (params) => params.data[params.colDef.field].value,
    valueSetter: syncValidateValueSetter(validation),
    tooltipValueGetter: (params) => {
      if (!params.data[params.colDef.field].lastValidation || params.data[params.colDef.field].lastValidation === true) {
        return ('')
      } else {
        return (`ERROR: '${params.data[params.colDef.field].lastValidation}' was not a valid value for ${params.colDef.headerName}. ${validationFailedMessage}`)
      }
    },
    cellClassRules: {
      overridden: isCellOverridden,
      validationFailed: (params) => {
        return (params.data[params.colDef.field].lastValidation && params.data[params.colDef.field].lastValidation !== true)
      }
    },
    ...additionalOptions
  }

  return (options)
}

const createReadOnlyColumn = (columnName, field, width, columnType, additionalOptions) => {
  const [type, filter, formatter] = processColumnType(columnType)

  const options = {
    headerName: columnName,
    field: field,
    type: type,
    width: width,
    editable: false,
    sortable: true,
    cellClass: ['readonly'], // this overrides any class rules set by things like numericColumn :-(, so we have to add it ourselves if we also need that
    cellClassRules: {
      'ag-right-aligned-cell': () => {
        return (columnType === ColumnTypes.Currency || columnType === ColumnTypes.Percent || columnType === ColumnTypes.Number)
      }
    },
    filter: filter,
    valueFormatter: formatter,
    ...additionalOptions
  }

  return (options)
}

const createCheckboxColumn = (columnName, field, width, additionalOptions) => {
  const options = {
    headerName: columnName,
    field: field,
    editable: true,
    sortable: true,
    filter: 'agSetColumnFilter',
    cellRenderer: 'checkboxRenderer', // use our custom registered Checkbox renderer
    cellStyle: { textAlign: 'center' },
    cellClassRules: {
      overridden: isCellOverridden
    },
    valueGetter: (params) => params.data[params.colDef.field].value,
    valueSetter: (params) => {
      params.data[params.colDef.field].value = params.newValue
      return (true)
    },
    ...additionalOptions
  }

  return (options)
}

const validateMarkup = (value, params) => {
  const calculationType = params.data.markup_calculation_type.value

  if (calculationType.includes('Percent')) {
    const floatingPointValue = parseFloat(value)

    if (isNaN(floatingPointValue)) {
      return (false)
    } else {
      return (true)
    }
  } else {
    const integerRegex = /^\d{1,9}$/

    const match = integerRegex.test(value)

    return (match)
  }
}
